import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { clearError, clearSessionExpiredMessage, loginWithTokens } from '../../store/slices/authSlice';
import api from '../../services/api';
import './Login.css';

const Login = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { error } = useSelector((state) => state.auth);

  const [sessionMessage, setSessionMessage] = useState(null);
  const [msLoading, setMsLoading] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);
  const [loginError, setLoginError] = useState(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    const redirectTarget = location.state?.from;
    if (redirectTarget) {
      sessionStorage.setItem('postLoginRedirect', redirectTarget);
    }

    const storedMessage = sessionStorage.getItem('sessionMessage');
    if (storedMessage) {
      setSessionMessage(storedMessage);
      sessionStorage.removeItem('sessionMessage');
    }

    dispatch(clearSessionExpiredMessage());

    return () => {
      dispatch(clearError());
      setSessionMessage(null);
    };
  }, [dispatch, location.state]);

  const handleMicrosoftLogin = async () => {
    setMsLoading(true);
    setLoginError(null);

    try {
      const redirectAfter =
        sessionStorage.getItem('postLoginRedirect') || '/dashboard';

      const response = await api.get('/auth/microsoft/login', {
        params: { redirect_after: redirectAfter },
      });

      const authUrl = response.data?.auth_url;
      if (!authUrl) {
        throw new Error('No authorization URL returned from server');
      }

      window.location.href = authUrl;
    } catch (err) {
      setMsLoading(false);
      const msg =
        err.response?.data?.detail ||
        err.message ||
        'Failed to initiate Microsoft login. Please try again.';
      setLoginError(msg);
    }
  };

  const handleLocalLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setLoginError('Please enter your email and password.');
      return;
    }

    setLocalLoading(true);
    setLoginError(null);

    try {
      const response = await api.post('/auth/login', { email, password });
      const { access_token, refresh_token } = response.data;

      // Store tokens
      localStorage.setItem('accessToken', access_token);
      localStorage.setItem('refreshToken', refresh_token);

      // Fetch user profile
      const userResponse = await api.get('/auth/me');
      localStorage.setItem('user', JSON.stringify(userResponse.data));

      // Bootstrap Redux auth state (PublicRoute / ProtectedRoute handle redirect)
      await dispatch(loginWithTokens()).unwrap();
    } catch (err) {
      setLocalLoading(false);
      const msg =
        err.response?.data?.detail || 'Invalid email or password. Please try again.';
      setLoginError(msg);
    }
  };

  return (
    <div className="auth-form">
      <h2>Sign In</h2>

      {sessionMessage && (
        <div className="session-expired-message">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
            <path d="M12 8V12M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <span>{sessionMessage}</span>
        </div>
      )}

      {(error || loginError) && (
        <div className="form-error login-error-banner">
          {error || loginError}
        </div>
      )}

      {/* Email / Password form for local accounts */}
      <form onSubmit={handleLocalLogin} noValidate>
        <div className="form-group" style={{ marginBottom: 'var(--space-3)' }}>
          <label className="form-label" htmlFor="login-email">Email</label>
          <input
            id="login-email"
            type="email"
            className="form-input"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            disabled={localLoading || msLoading}
          />
        </div>
        <div className="form-group" style={{ marginBottom: 'var(--space-4)' }}>
          <label className="form-label" htmlFor="login-password">Password</label>
          <input
            id="login-password"
            type="password"
            className="form-input"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            disabled={localLoading || msLoading}
          />
        </div>
        <button
          type="submit"
          className="btn btn-primary"
          style={{ width: '100%', justifyContent: 'center' }}
          disabled={localLoading || msLoading}
        >
          {localLoading ? (
            <>
              <span className="spinner" style={{ borderTopColor: '#fff', borderColor: 'rgba(255,255,255,0.3)', width: '16px', height: '16px', marginRight: '8px' }} />
              Signing in...
            </>
          ) : (
            'Sign In'
          )}
        </button>
      </form>

      {/* Divider */}
      <div className="login-divider">
        <span>or</span>
      </div>

      {/* Microsoft SSO */}
      <button
        type="button"
        className="btn-microsoft"
        onClick={handleMicrosoftLogin}
        disabled={localLoading || msLoading}
        aria-label="Sign in with Microsoft"
      >
        {msLoading ? (
          <>
            <span className="spinner spinner-dark" />
            Redirecting to Microsoft...
          </>
        ) : (
          <>
            <svg className="microsoft-logo" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 21 21" width="21" height="21" aria-hidden="true">
              <rect x="1" y="1" width="9" height="9" fill="#F25022" />
              <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
              <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
              <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
            </svg>
            Sign in with Microsoft
          </>
        )}
      </button>

      <p className="login-sso-note">
        Use your Microsoft account or the email and password provided by your administrator.
      </p>
    </div>
  );
};

export default Login;
